import { ArrowDown, Reply, Smile } from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import type { ChatMessage, ChatSnapshot } from "./types";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "😮", "🔥", "🏇"] as const;

export function MessageFeed({
  snapshot,
  onLoadOlder,
  onReact,
  onReply,
}: {
  snapshot: ChatSnapshot;
  onLoadOlder: () => void;
  onReact?: (messageId: string, emoji: string) => void;
  onReply?: (message: ChatMessage) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const [nearBottom, setNearBottom] = useState(true);
  const [viewedMessageCount, setViewedMessageCount] = useState(0);
  const messages = useMemo(
    () => snapshot.messages.slice(-100),
    [snapshot.messages],
  );
  const newCount = nearBottom
    ? 0
    : Math.max(0, messages.length - viewedMessageCount);

  useEffect(() => {
    if (!nearBottomRef.current && !messages.at(-1)?.isSelf) return;
    const container = containerRef.current;
    if (!container) return;
    const scroll = () => {
      container.scrollTop = container.scrollHeight;
    };
    scroll();
    let settleFrame = 0;
    let cancelled = false;
    const layoutFrame = window.requestAnimationFrame(() => {
      scroll();
      settleFrame = window.requestAnimationFrame(scroll);
    });
    void document.fonts?.ready.then(() => {
      if (!cancelled) scroll();
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(layoutFrame);
      window.cancelAnimationFrame(settleFrame);
    };
  }, [messages]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    let frame = 0;
    const preserveBottom = () => {
      if (!nearBottomRef.current) return;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    };
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(preserveBottom);
    observer?.observe(container);
    window.addEventListener("resize", preserveBottom);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", preserveBottom);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  function statusText() {
    if (snapshot.status === "unavailable") {
      return "Chat is ready for its Channels connection.";
    }
    if (snapshot.status === "connecting") return "Connecting to chat…";
    if (snapshot.status === "error") {
      return snapshot.errorMessage ?? "Chat is temporarily unavailable.";
    }
    return "No messages yet";
  }

  return (
    <div className="chat-feed">
      {snapshot.hasOlder ? (
        <button
          className="chat-feed__older"
          disabled={snapshot.loadingOlder}
          onClick={onLoadOlder}
          type="button"
        >
          {snapshot.loadingOlder ? "Loading…" : "Load earlier messages"}
        </button>
      ) : null}
      <div
        className="chat-feed__scroll"
        onScroll={(event) => {
          const target = event.currentTarget;
          const atBottom =
            target.scrollHeight - target.scrollTop - target.clientHeight < 48;
          if (atBottom || (nearBottom && !atBottom)) {
            setViewedMessageCount(messages.length);
          }
          nearBottomRef.current = atBottom;
          setNearBottom(atBottom);
        }}
        ref={containerRef}
      >
        {messages.length === 0 ? (
          <p className="chat-feed__state">{statusText()}</p>
        ) : (
          messages.map((message, index) => {
            const day = dateKey(message.timestamp);
            const previousDay =
              index > 0 ? dateKey(messages[index - 1].timestamp) : null;
            return (
              <Fragment key={message.id}>
                {day !== previousDay ? (
                  <DateSeparator timestamp={message.timestamp} />
                ) : null}
                <MessageBubble
                  message={message}
                  onReact={onReact}
                  onReply={onReply}
                />
              </Fragment>
            );
          })
        )}
      </div>
      {!nearBottom ? (
        <button
          aria-label={
            newCount > 0 ? `${newCount} new messages` : "Jump to latest"
          }
          className="chat-feed__latest"
          onClick={() => {
            const container = containerRef.current;
            if (container) container.scrollTop = container.scrollHeight;
            setViewedMessageCount(messages.length);
            nearBottomRef.current = true;
            setNearBottom(true);
          }}
          type="button"
        >
          {newCount > 0 ? (
            <span className="tabular-nums">{newCount} new</span>
          ) : null}
          <ArrowDown aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

function MessageBubble({
  message,
  onReact,
  onReply,
}: {
  message: ChatMessage;
  onReact?: (messageId: string, emoji: string) => void;
  onReply?: (message: ChatMessage) => void;
}) {
  const [reactionOpen, setReactionOpen] = useState(false);
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(
    null,
  );
  if (message.type === "big_win") {
    return <BigWinCard message={message} onReply={onReply} />;
  }

  const avatarUrl = safeChatHref(message.avatarUrl);
  const avatarBorderUrl = safeChatHref(message.avatarBorderUrl);
  const tierIconUrl = safeChatHref(message.tierIconUrl);
  return (
    <article
      className={[
        "chat-message",
        message.isSelf && "is-self",
        message.mentionedViewer && "mentions-viewer",
        message.type === "announcement" && "is-announcement",
      ]
        .filter(Boolean)
        .join(" ")}
      data-message-id={message.id}
      onPointerUp={(event) => {
        if (
          event.pointerType !== "touch" ||
          !onReply ||
          (event.target instanceof Element &&
            event.target.closest("button, a, [data-player-username]"))
        ) {
          return;
        }
        const previous = lastTapRef.current;
        const now = performance.now();
        if (
          previous &&
          now - previous.time <= 300 &&
          Math.hypot(event.clientX - previous.x, event.clientY - previous.y) <=
            30
        ) {
          lastTapRef.current = null;
          onReply(message);
          document
            .querySelector<HTMLTextAreaElement>(".chat-composer textarea")
            ?.focus();
          return;
        }
        lastTapRef.current = {
          time: now,
          x: event.clientX,
          y: event.clientY,
        };
      }}
    >
      <div className="chat-message__identity">
        <span
          className={["chat-message__avatar", avatarBorderUrl && "is-framed"]
            .filter(Boolean)
            .join(" ")}
        >
          {avatarUrl ? (
            <img alt="" src={avatarUrl} />
          ) : (
            <img alt="" src="/app/static/themes/monkeytilt/logo-head.webp" />
          )}
          {avatarBorderUrl ? (
            <img
              alt=""
              className="chat-message__avatar-border"
              src={avatarBorderUrl}
            />
          ) : null}
        </span>
        <strong
          data-player-username={message.username}
          style={message.nameColor ? { color: message.nameColor } : undefined}
        >
          {message.displayName}
        </strong>
        {tierIconUrl ? (
          <img alt="" className="chat-message__tier" src={tierIconUrl} />
        ) : null}
        {message.badges?.map((badge) => (
          <button
            aria-label={badge.label}
            className="chat-message__badge"
            key={badge.label}
            title={badge.label}
            type="button"
          >
            <span>
              {safeChatHref(badge.iconUrl) ? (
                <img alt="" src={safeChatHref(badge.iconUrl) ?? undefined} />
              ) : (
                badge.label.slice(0, 2)
              )}
            </span>
          </button>
        ))}
        {message.title ? (
          <small
            style={{
              backgroundColor: message.title.backgroundColor,
              color: message.title.color,
            }}
            title={message.title.label}
          >
            {message.title.label}
          </small>
        ) : message.isStaff ? (
          <small>STAFF</small>
        ) : null}
      </div>
      {message.replyTo ? (
        <button
          aria-label="Jump to replied message"
          className="chat-message__reply"
          onClick={() => jumpToMessage(message.replyTo?.id)}
          type="button"
        >
          <strong>{message.replyTo.displayName}</strong>
          <span>
            {message.replyTo.deleted
              ? "Message deleted"
              : message.replyTo.excerpt}
          </span>
        </button>
      ) : null}
      <p>
        {message.deleted ? (
          <em>Message deleted</em>
        ) : (
          renderMessageText(
            message.text,
            message.type === "announcement" || Boolean(message.isStaff),
          )
        )}
      </p>
      {message.reactions?.length ? (
        <div className="chat-message__reactions">
          {message.reactions.map((reaction) => (
            <button
              aria-label={`${reaction.emoji} ${reaction.count}`}
              className={reaction.reactedByViewer ? "is-viewer" : ""}
              key={reaction.emoji}
              onClick={() => onReact?.(message.id, reaction.emoji)}
              type="button"
            >
              {reaction.emoji} {reaction.count}
            </button>
          ))}
          {onReact ? (
            <ReactionPicker
              messageId={message.id}
              onReact={onReact}
              open={reactionOpen}
              setOpen={setReactionOpen}
            />
          ) : null}
        </div>
      ) : onReact && !message.deleted ? (
        <div className="chat-message__reactions">
          <ReactionPicker
            messageId={message.id}
            onReact={onReact}
            open={reactionOpen}
            setOpen={setReactionOpen}
          />
        </div>
      ) : null}
      <time className="chat-message__time" dateTime={message.timestamp}>
        {formatTime(message.timestamp)}
      </time>
      {onReply && !message.deleted && message.type !== "announcement" ? (
        <button
          aria-label={`Reply to ${message.displayName}`}
          className="chat-message__reply-button"
          onClick={() => onReply(message)}
          type="button"
        >
          <Reply aria-hidden />
        </button>
      ) : null}
    </article>
  );
}

function BigWinCard({
  message,
  onReply,
}: {
  message: ChatMessage;
  onReply?: (message: ChatMessage) => void;
}) {
  const tier =
    (message.multiplier ?? 0) >= 1000
      ? "gold"
      : (message.multiplier ?? 0) >= 500
        ? "yellow"
        : "green";
  const imageUrl = safeChatHref(message.gameImageUrl);
  const content = (
    <>
      {imageUrl ? <img alt="" src={imageUrl} /> : null}
      <div>
        <strong>{message.gameName ?? "Casino Game"}</strong>
        <span>{message.displayName || "Anonymous"} hit</span>
      </div>
      {message.multiplier ? (
        <b>{formatMultiplier(message.multiplier)}</b>
      ) : null}
      {onReply ? (
        <button
          aria-label={`Reply to ${message.displayName}`}
          className="chat-win-card__reply"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onReply(message);
            document
              .querySelector<HTMLTextAreaElement>(".chat-composer textarea")
              ?.focus();
          }}
          type="button"
        >
          <Reply aria-hidden />
        </button>
      ) : null}
    </>
  );
  const href = safeChatHref(message.gameHref);
  return href ? (
    <a
      className={`chat-win-card is-${tier}`}
      data-message-id={message.id}
      href={href}
      title={message.gameName ?? "Casino Game"}
    >
      {content}
    </a>
  ) : (
    <article
      className={`chat-win-card is-${tier}`}
      data-message-id={message.id}
    >
      {content}
    </article>
  );
}

function ReactionPicker({
  messageId,
  onReact,
  open,
  setOpen,
}: {
  messageId: string;
  onReact: (messageId: string, emoji: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  return (
    <>
      <button
        aria-expanded={open}
        aria-label="Add reaction"
        onClick={() => setOpen(!open)}
        type="button"
      >
        <Smile aria-hidden />
      </button>
      {open ? (
        <span className="chat-message__reaction-picker" role="menu">
          {QUICK_REACTIONS.map((emoji) => (
            <button
              aria-label={`React ${emoji}`}
              key={emoji}
              onClick={() => {
                onReact(messageId, emoji);
                setOpen(false);
              }}
              role="menuitem"
              type="button"
            >
              {emoji}
            </button>
          ))}
        </span>
      ) : null}
    </>
  );
}

function jumpToMessage(messageId?: string) {
  if (!messageId) return;
  const target = document.querySelector<HTMLElement>(
    `[data-message-id="${messageId}"]`,
  );
  if (!target) return;
  target.scrollIntoView({ behavior: "smooth", block: "center" });
  target.classList.remove("chat-message--jumped");
  void target.offsetWidth;
  target.classList.add("chat-message--jumped");
  window.setTimeout(
    () => target.classList.remove("chat-message--jumped"),
    2600,
  );
}

function DateSeparator({ timestamp }: { timestamp: string }) {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const label =
    date.toDateString() === today.toDateString()
      ? "Today"
      : date.toDateString() === yesterday.toDateString()
        ? "Yesterday"
        : new Intl.DateTimeFormat(undefined, {
            month: "long",
            day: "numeric",
          }).format(date);
  return (
    <div className="chat-date-separator">
      <span>{label}</span>
    </div>
  );
}

function renderMessageText(text: string, linksEnabled: boolean) {
  return text
    .split(/(https?:\/\/[^\s]+|@[a-zA-Z0-9_]{2,30})/g)
    .filter(Boolean)
    .map((segment, index) => {
      if (linksEnabled && /^https?:\/\//.test(segment)) {
        return (
          <a
            href={segment}
            key={`${segment}-${index}`}
            rel="noopener noreferrer nofollow"
            target="_blank"
          >
            {segment}
          </a>
        );
      }
      if (segment.startsWith("@")) {
        return (
          <span className="chat-message__mention" key={`${segment}-${index}`}>
            {segment}
          </span>
        );
      }
      return segment;
    });
}

function dateKey(timestamp: string) {
  const date = new Date(timestamp);
  return Number.isNaN(date.valueOf()) ? "" : date.toISOString().slice(0, 10);
}

function formatTime(timestamp: string) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.valueOf())) return "";
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function safeChatHref(value?: string | null) {
  if (!value) return null;
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

function formatMultiplier(value: number) {
  return `${Math.max(0, Math.round(value)).toLocaleString()}x`;
}
